// Транспорт и хуки раздела «Ведомость инженера» — баллы, бейджи и выплаты портала 73.
// ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/p73.ts и не api/p73x.ts. В первом — инженерный блок того же
// портала (работы по судам), он открыт всем ролям CRM. Во втором — овертаймы и суточные.
// Здесь третье: сколько человеку НАЧИСЛЕНО баллов и сколько ему ЕЩЁ ДОЛЖНЫ. Разряд данных
// тот же, что у кассы и ФОТ, а дверь — одна учётка владельца (rights.владелец, раздел
// 'engineer-ledger'). Сложить три файла в один значило бы однажды переиспользовать хук
// не там и открыть заработок людей тому, кто зашёл посмотреть акты.
//
// Начисляет и выплачивает по-прежнему портал 73. Здесь нет ни одной записи, и появиться
// она не может: начисление сцеплено с верификациями, курсом и бизнес-процессами портала
// (см. шапку p73g_api.py, «ЗДЕСЬ НЕЛЬЗЯ НАЧИСЛИТЬ»).

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/p73g'

/**
 * Тот же разбор ответа, что в api/client.ts, api/p73.ts, api/p73x.ts и api/pbh.ts.
 *
 * 400 от ядра здесь почти всегда одно и то же: «назовите человека либо запросите свод по
 * всем явно, all=1». Это ОТКАЗ, а не «начислений нет», и превращать его в пустую таблицу
 * нельзя — пустая таблица про деньги читается как «человеку ничего не должны».
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    if (res.status === 403) {
      // BFF отказал по учётке, а не по роли. Человеку надо сказать это словами: иначе
      // «forbidden» читается как поломка, и он идёт чинить то, что работает как задумано.
      throw new Error('раздел открыт одной учётке владельца — здесь заработок людей '
                      + 'поимённо, тот же разряд данных, что касса и ФОТ')
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. p73g_api.py) ───────────

/** Общая обёртка списка ядра + пометка «здесь деньги нескольких людей». */
export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  restricted?: string
  restricted_why?: string
  note?: string
}

/** Строка ведомости: кому сколько начислено, выплачено и сколько должны. */
export interface ВедомостьСтрока {
  d1_id: number
  full_name: string
  bitrix_user_id: number | null
  level: number | null
  role: string | null
  is_engineer: boolean | null
  is_active: boolean | null
  total_points: number
  paid_points: number
  /** Остаток. Ядро считает его разностью и показывает вместе с обеими частями. */
  available_points: number
  current_streak: number | null
  longest_streak: number | null
  login_streak: number | null
  last_login_at: string | null
  course73_done_at: string | null
  accruals: number
  accrued_sum: number
  last_accrual_at: string | null
  badges: number
  payouts: number
  paid_rub: number
  pending_requests: number
  /**
   * Баланс минус журнал. Ноль у 141 учётки из 142 (на 28.07.2026); отличное от нуля значит,
   * что портал записал начисление в одно место и не записал в другое (общей транзакции
   * у него нет).
   */
  total_minus_log: number
}

export interface ВедомостьИтоги {
  people: number
  total_points: number
  paid_points: number
  available_points: number
  people_owed: number
  people_negative: number
  people_earning: number
}

/** Кого спрятало умолчание who=active. Считается ядром на тот же запрос. */
export interface Скрытые {
  people: number
  total_points: number
  available_points: number
  who: string
}

export interface НачислениеСтрока {
  d1_id: number
  user_d1_id: number
  full_name: string
  bitrix_user_id: number | null
  is_active: boolean | null
  points: number
  /** Текст причины как его написал портал. Кода причины в источнике нет вовсе. */
  reason: string | null
  created_at: string | null
  verification_d1_id: number | null
  /** Вид начисления — ДОГАДКА ядра по началу текста причины, а не поле источника. */
  kind: string
  period: string | null
}

export interface НачисленияИтоги {
  accruals: number
  points: number
  people: number
  first_at: string | null
  last_at: string | null
  zero_points: number
  negative: number
  /** Сколько строк разбор не узнал. Ноль сегодня — не обещание на завтра. */
  unparsed_kind: number
}

export interface БейджСтрока {
  d1_id: number
  code: string | null
  name: string | null
  description: string | null
  icon: string | null
  bonus_points: number | null
  sort: number | null
  awarded: number
  people: number
  last_awarded_at: string | null
  /** Сегодняшняя ставка × число выдач. Не то же, что начислено по журналу. */
  cost_by_rate: number
}

export interface РейтингСтрока {
  d1_id: number
  full_name: string
  bitrix_user_id: number | null
  level: number | null
  current_streak: number | null
  longest_streak: number | null
  badges: number
  period_accruals: number
  verifications: number
  rank: number
  /** Дальше — только при money=1. Без него этих полей в ответе НЕТ вовсе. */
  total_points?: number
  available_points?: number
  period_points?: number
}

export interface ВыплатаСтрока {
  d1_id: number
  user_d1_id: number
  full_name: string
  bitrix_user_id: number | null
  amount_rub: number
  points_paid: number | null
  period: string | null
  notes: string | null
  created_at: string | null
  created_by_d1_id: number | null
  created_by_name: string
  /** Рубли минус баллы. Не ноль — курс в этой строке был не 1:1 либо это опечатка. */
  rub_minus_points: number
}

export interface ВыплатыИтоги {
  payouts: number
  people: number
  amount_rub: number
  points_paid: number
  periods: number
  first_at: string | null
  last_at: string | null
}

export interface ЗаявкаСтрока {
  d1_id: number
  user_d1_id: number
  full_name: string
  bitrix_user_id: number | null
  points: number
  amount_rub: number
  status: string | null
  notes: string | null
  created_at: string | null
  reviewed_at: string | null
  reviewed_by_d1_id: number | null
  reviewed_by_name: string
  hours_to_review: number | null
  /** Одобрена, а выплаты на эту сумму нет. Вопрос, а не приговор: сверка по паре. */
  approved_without_payout: boolean | null
}

export interface ЗаявкиИтоги {
  requests: number
  people: number
  amount_rub: number
  pending: number
  pending_rub: number
  approved: number
  rejected: number
  first_at: string | null
  last_at: string | null
}

/** Одна проверка сходимости: число строк и примеры. Ядро ничего не чинит. */
export interface ПроверкаСтрока {
  check: string
  title: string
  why: string
  rows: number
  samples: Record<string, unknown>[]
  sample_limit: number
}

export interface Сходимость {
  rows: ПроверкаСтрока[]
  total: number
  checks_with_findings: number
  note: string
  restricted?: string
  restricted_why?: string
}

export interface СвежестьP73G {
  entity: string
  last_sync_at: string | null
  last_full: string | null
  last_count: number | null
  age_min: number | null
}

/** Счётчики раздела. Считаются ОДНИМ запросом — иначе они не согласованы между собой. */
export interface СчётчикиP73G {
  accounts: number
  accounts_active: number
  people_earning: number
  /** Начислено ПО БАЛАНСАМ (users.total_points). */
  total_points: number
  paid_points: number
  available_points: number
  accruals: number
  /** Начислено ПО ЖУРНАЛУ (bonus_log). С предыдущим сходиться не обязано — и не сходится. */
  accrued_sum: number
  history_from: string | null
  last_accrual_at: string | null
  badges: number
  badges_never: number
  badges_awarded: number
  people_with_badge: number
  payouts: number
  payouts_rub: number
  requests_pending: number
  requests_pending_rub: number
  /** Курс посчитан по проведённым выплатам, а не взят из настроек портала. */
  rub_per_point: number | null
  hidden_accounts: number
  hidden_points: number
}

export interface ПериодP73G {
  period: string
  accruals: number
  points: number
  people: number
}

export interface ВидНачисления {
  kind: string
  accruals: number
  points: number
}

export interface ЧеловекP73G {
  id: number
  name: string
  bitrix_user_id: number | null
  is_active: boolean | null
  is_engineer: boolean | null
  level: number | null
}

export interface СправочникиP73G {
  freshness: СвежестьP73G[]
  counts: СчётчикиP73G
  periods: ПериодP73G[]
  kinds: ВидНачисления[]
  /** Список для фильтра. Без единой суммы — иначе это был бы обход двери «назовите кого». */
  people: ЧеловекP73G[]
  /** Оговорки о данных с УЖЕ ПОДСТАВЛЕННЫМИ сегодняшними числами. */
  caveats: string[]
  kind_names: Record<string, string>
  status_names: Record<string, string>
  level_names: Record<string, string>
  who_names: Record<string, string>
  source: string
}

// ─────────── запросы ───────────

export interface ФильтрыP73G {
  /** Индекс нужен, чтобы фильтры уходили в строкаЗапроса() как обычный набор пар. */
  [ключ: string]: unknown
  engineer?: number | string
  engineer_bx?: number | string
  all?: boolean
  who?: string
  sort?: string
  kind?: string
  period?: string
  status?: string
  owed?: boolean
  pending?: boolean
  money?: boolean
  q?: string
  from?: string
  to?: string
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useP73GRefs() {
  return useQuery<СправочникиP73G>({
    queryKey: ['p73g', 'refs'],
    queryFn: () => запрос<СправочникиP73G>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useВедомость(ф: ФильтрыP73G) {
  return useQuery<Список<ВедомостьСтрока> & {
    totals: ВедомостьИтоги; hidden: Скрытые; who: string; sort: string
  }>({
    queryKey: ['p73g', 'ledger', ф],
    queryFn: () => запрос('/ledger' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useНачисления(ф: ФильтрыP73G) {
  return useQuery<Список<НачислениеСтрока> & { totals: НачисленияИтоги; kind: string }>({
    queryKey: ['p73g', 'accruals', ф],
    queryFn: () => запрос('/accruals' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useБейджи(ф: ФильтрыP73G) {
  return useQuery<Список<БейджСтрока> & { kind: string; sort: string }>({
    queryKey: ['p73g', 'badges', ф],
    queryFn: () => запрос('/badges' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useРейтинг(ф: ФильтрыP73G) {
  return useQuery<Список<РейтингСтрока> & { period: string; money: boolean
                                            differs_from_portal: string[] }>({
    queryKey: ['p73g', 'leaderboard', ф],
    queryFn: () => запрос('/leaderboard' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useВыплаты(ф: ФильтрыP73G) {
  return useQuery<Список<ВыплатаСтрока> & { totals: ВыплатыИтоги }>({
    queryKey: ['p73g', 'payouts', ф],
    queryFn: () => запрос('/payouts' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useЗаявки(ф: ФильтрыP73G) {
  return useQuery<Список<ЗаявкаСтрока> & { totals: ЗаявкиИтоги }>({
    queryKey: ['p73g', 'payout-requests', ф],
    queryFn: () => запрос('/payout-requests' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/**
 * Сходимость ведомости. Всегда по всем и всегда с ФИО — поэтому all=1 подставляется здесь,
 * а не на экране: забыть его значит получить 400 вместо девяти проверок.
 */
export function useСходимость(ф: ФильтрыP73G = {}) {
  return useQuery<Сходимость>({
    queryKey: ['p73g', 'checks', ф],
    queryFn: () => запрос('/checks' + строкаЗапроса({ ...ф, all: true })),
    ...ОБЩЕЕ,
  })
}
